import { z } from 'zod';
export var ChannelSenderEnum;
(function (ChannelSenderEnum) {
    ChannelSenderEnum["EMAIL"] = "email";
    ChannelSenderEnum["SMS"] = "sms";
})(ChannelSenderEnum || (ChannelSenderEnum = {}));
export var ChannelAccountVendorEnum;
(function (ChannelAccountVendorEnum) {
    ChannelAccountVendorEnum["POSTMARK"] = "postmark";
    ChannelAccountVendorEnum["SES"] = "ses";
    ChannelAccountVendorEnum["TWILIO"] = "twilio";
})(ChannelAccountVendorEnum || (ChannelAccountVendorEnum = {}));
export var ChannelAccountProvisionerEnum;
(function (ChannelAccountProvisionerEnum) {
    ChannelAccountProvisionerEnum["REACH"] = "reach";
    ChannelAccountProvisionerEnum["BYO"] = "byo";
})(ChannelAccountProvisionerEnum || (ChannelAccountProvisionerEnum = {}));
/**
 * RFC 5321 dot-atom local-part: one or more atext runs separated by single dots,
 * no leading/trailing/consecutive dots, and capped at 64 characters.
 */
// The `/ { } | -` escapes below are redundant for JS RegExp matching, but
// `.source` is fed to HTML <input pattern>, which browsers compile with the `v`
// (unicodeSets) flag — and under `v` those characters must be escaped inside a
// character class or the whole pattern is rejected and validation is skipped.
export const EMAIL_USER_PART_REGEX = 
// eslint-disable-next-line no-useless-escape -- required for the v-flag pattern attribute
/^[A-Za-z0-9!#$%&'*+\/=?^_`\{\|\}~\-]+(?:\.[A-Za-z0-9!#$%&'*+\/=?^_`\{\|\}~\-]+)*$/;
export const EMAIL_USER_PART_MAX_LENGTH = 64;
export const emailUserPartSchema = z
    .string()
    .min(1, 'Required')
    .max(EMAIL_USER_PART_MAX_LENGTH, `Must be ${EMAIL_USER_PART_MAX_LENGTH} characters or less`)
    .regex(EMAIL_USER_PART_REGEX, 'Only letters, numbers, and standard email characters allowed');
/**
 * Lenient (RFC-lite) email address: a non-empty local part, an `@`, and a
 * dotted domain. Import this rather than re-declaring the pattern.
 */
export const EMAIL_ADDRESS_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const emailAddressSchema = z
    .string()
    .trim()
    .regex(EMAIL_ADDRESS_REGEX, 'Please enter a valid email address');
/**
 * A single DNS label — e.g. the `send` in `send.acme.com` (RFC 1035): 2–63
 * characters, letters/numbers/hyphens, starting and ending alphanumeric.
 */
export const MAX_SUBDOMAIN_LENGTH = 63;
// eslint-disable-next-line no-useless-escape -- escaped `-` required for the v-flag pattern attribute (see EMAIL_USER_PART_REGEX)
export const SUBDOMAIN_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9\-]*[a-zA-Z0-9]$/;
export const subdomainSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(2, 'Subdomain must be at least 2 characters')
    .max(MAX_SUBDOMAIN_LENGTH, `Subdomain must be ${MAX_SUBDOMAIN_LENGTH} characters or less`)
    .regex(SUBDOMAIN_REGEX, 'Only letters, numbers, and hyphens. Must start and end with a letter or number.');
/**
 * A public apex/root domain — e.g. `acme.com` or `acme.co.uk`. Lowercase-only;
 * `apexDomainSchema` normalizes input before testing. No Public Suffix List
 * check, so `acme.evil` passes — callers needing registrability check separately.
 */
export const APEX_DOMAIN_REGEX = 
// eslint-disable-next-line no-useless-escape -- escaped `-` required for the v-flag pattern attribute (see EMAIL_USER_PART_REGEX)
/^[a-z0-9]([a-z0-9\-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9\-]{0,61}[a-z0-9])?)+$/;
export const apexDomainSchema = z
    .string()
    .trim()
    .toLowerCase()
    .regex(APEX_DOMAIN_REGEX, 'Enter a valid domain, e.g. example.com');
export const sendingDomainDnsRecordSchema = z.object({
    type: z.enum(['TXT', 'CNAME', 'MX']),
    name: z.string(),
    value: z.string(),
    priority: z.number().optional(),
});
// Entri's DNS-record shape for its setup modal: hosts are relative to the apex
// (root suffix stripped) and carry an explicit TTL.
export const entriDnsRecordSchema = z.object({
    type: z.enum(['TXT', 'CNAME', 'MX']),
    host: z.string(),
    value: z.string(),
    ttl: z.number(),
    priority: z.number().optional(),
});
export var SendingDomainStatusEnum;
(function (SendingDomainStatusEnum) {
    SendingDomainStatusEnum["NEEDS_ATTENTION"] = "needs_attention";
    SendingDomainStatusEnum["PENDING_VERIFICATION"] = "pending_verification";
    SendingDomainStatusEnum["VERIFIED"] = "verified";
})(SendingDomainStatusEnum || (SendingDomainStatusEnum = {}));
export const domainVerificationFlagsSchema = z.object({
    dkimVerified: z.boolean().optional(),
    dmarcVerified: z.boolean().optional(),
    verifiedReturnPath: z.boolean().optional(),
});
export const sendingDomainVerificationResultSchema = domainVerificationFlagsSchema.required().extend({
    status: z.nativeEnum(SendingDomainStatusEnum),
});
export const channelAccountSummarySchema = z.object({
    id: z.string(),
    domain: z.string().nullable(),
    status: z.nativeEnum(SendingDomainStatusEnum).nullable(),
    isTenantSupplied: z.boolean(),
    medium: z.nativeEnum(ChannelSenderEnum),
    vendor: z.nativeEnum(ChannelAccountVendorEnum),
    postmarkServerId: z.number().nullable(),
    postmarkDomainId: z.number().nullable(),
});
export const channelAccountSummaryListSchema = z.array(channelAccountSummarySchema);
export const reverifyChannelAccountBodySchema = z.object({
    businessId: z.string().uuid(),
});
//# sourceMappingURL=channels.js.map